export type SavedCategoryAttributeRow = {
  id?: number | null
  attribute_field?: unknown
  updated_at?: string | null
}

const toFieldId = (value: unknown): number | null => {
  if (value == null || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const updatedAtMs = (row: SavedCategoryAttributeRow): number => {
  if (!row.updated_at) return Number.NEGATIVE_INFINITY
  const ms = Date.parse(row.updated_at)
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY
}

const savedRowId = (row: SavedCategoryAttributeRow): number => {
  const id = Number(row.id)
  return Number.isFinite(id) ? id : Number.NEGATIVE_INFINITY
}

/** True when `next` is the row UI should read and the next PUT should write. */
export const isNewerSavedAttributeRow = (
  next: SavedCategoryAttributeRow,
  current: SavedCategoryAttributeRow
): boolean => {
  const nextUpdated = updatedAtMs(next)
  const currentUpdated = updatedAtMs(current)
  if (nextUpdated !== currentUpdated) return nextUpdated > currentUpdated
  return savedRowId(next) > savedRowId(current)
}

/** Catalog field id from a saved row (`attribute_field` may be an object or a raw id). */
export const getCategoryAttributeFieldId = (attributeField: unknown): number | null => {
  if (attributeField == null || attributeField === '') return null
  if (typeof attributeField === 'object') {
    return toFieldId((attributeField as { id?: unknown }).id)
  }
  return toFieldId(attributeField)
}

/**
 * One saved mapping row per catalog field. If duplicates exist, keep the
 * most recently updated row so hydrate and PUT target the same row.
 * `updated_at` wins; `id` is only a tie-break. Never use array index.
 */
export const indexLatestSavedAttributesByField = <T extends SavedCategoryAttributeRow>(
  rows: T[] | null | undefined
): Map<number, T> => {
  const latestByField = new Map<number, T>()
  for (const row of rows ?? []) {
    const fieldId = getCategoryAttributeFieldId(row.attribute_field)
    if (fieldId == null) continue
    const current = latestByField.get(fieldId)
    if (!current || isNewerSavedAttributeRow(row, current)) {
      latestByField.set(fieldId, row)
    }
  }
  return latestByField
}

export const getSavedCategoryAttributeRowId = (
  rows: SavedCategoryAttributeRow[] | null | undefined,
  attributeFieldId: number
): number | null => {
  const row = indexLatestSavedAttributesByField(rows).get(Number(attributeFieldId))
  return toFieldId(row?.id)
}

export type CategoryAttributeSaveRow = {
  attribute_field?: unknown
  value_str?: string | null
  value?: unknown
  mapped_field?: unknown
}

export type CategoryAttributeDefinition = {
  id: number
  isRequired?: boolean
  is_required?: boolean
  display_name?: string
  api_name?: string
  attribute_type?: string | null
}

export const REQUIRED_CATEGORY_ATTRIBUTES_SAVE_MESSAGE =
  'Please set a value for every required attribute before saving.'

export const CATEGORY_ATTRIBUTE_CATALOG_UNAVAILABLE_MESSAGE =
  'Category attributes could not be validated yet. Open Category Mapping and wait for attributes to load.'

const resolveCategoryAttributePayloadScalarId = (value: unknown): number | null => {
  if (value == null || value === '') return null
  if (typeof value === 'object') {
    return toFieldId((value as { id?: unknown }).id)
  }
  return toFieldId(value)
}

export const isCategoryAttributeDefinitionRequired = (
  definition: CategoryAttributeDefinition
): boolean => Boolean(definition.isRequired ?? definition.is_required)

export const categoryAttributePayloadHasValue = (
  attribute: CategoryAttributeSaveRow
): boolean => {
  const hasText =
    typeof attribute.value_str === 'string' && attribute.value_str.trim().length > 0
  const hasSelectValue = resolveCategoryAttributePayloadScalarId(attribute.value) != null
  const hasMapped = resolveCategoryAttributePayloadScalarId(attribute.mapped_field) != null
  return Boolean(hasText || hasSelectValue || hasMapped)
}

/** Legacy modal: allow saving when the attributes payload is completely empty on manual-map channels. */
export const platformAllowsEmptyCategoryAttributes = (
  platformCode: string,
  manualMapPlatforms: readonly string[]
): boolean =>
  manualMapPlatforms.map((platform) => platform.toUpperCase()).includes(platformCode.toUpperCase())

export const resolveCategoryAttributeCatalog = (
  liveCatalog: CategoryAttributeDefinition[] | null | undefined,
  snapshotCatalog: CategoryAttributeDefinition[] | null | undefined
): CategoryAttributeDefinition[] => {
  if (Array.isArray(liveCatalog) && liveCatalog.length > 0) {
    return liveCatalog
  }
  if (Array.isArray(snapshotCatalog) && snapshotCatalog.length > 0) {
    return snapshotCatalog
  }
  return []
}

export const shouldBlockSaveForUnavailableCategoryAttributeCatalog = (params: {
  categoryId: number | null
  isAttributeLoading: boolean
  categoryAttributes: CategoryAttributeDefinition[]
  selectedAttributes: CategoryAttributeSaveRow[]
}): boolean => {
  if (params.categoryId == null || params.isAttributeLoading) {
    return false
  }
  if (params.categoryAttributes.length > 0) {
    return false
  }
  return params.selectedAttributes.length > 0
}

/** First required catalog row (main or Extra) missing a default value or field mapping. */
export const getMissingRequiredCategoryAttribute = (params: {
  categoryAttributes: CategoryAttributeDefinition[] | null | undefined
  selectedAttributes: CategoryAttributeSaveRow[] | null | undefined
}): CategoryAttributeDefinition | null => {
  const categoryAttributes = Array.isArray(params.categoryAttributes)
    ? params.categoryAttributes
    : []
  if (!categoryAttributes.length) {
    return null
  }

  const rowByFieldId = indexLatestSavedAttributesByField(
    (params.selectedAttributes ?? []) as SavedCategoryAttributeRow[]
  )

  for (const definition of categoryAttributes) {
    if (!isCategoryAttributeDefinitionRequired(definition)) continue
    const row = rowByFieldId.get(definition.id)
    if (!row || !categoryAttributePayloadHasValue(row as CategoryAttributeSaveRow)) {
      return definition
    }
  }

  return null
}

export type CategoryAttributeSaveValidationResult =
  | { ok: true }
  | { ok: false; reason: 'catalog_unavailable' }
  | { ok: false; reason: 'missing_required'; attribute: CategoryAttributeDefinition }

/** Shared Active-save gate for listing profile create/update and category template modal. */
export const validateRequiredCategoryAttributesBeforeSave = (params: {
  categoryId: number | null
  isAttributeLoading: boolean
  categoryAttributes: CategoryAttributeDefinition[] | null | undefined
  categoryAttributeCatalogSnapshot?: CategoryAttributeDefinition[] | null | undefined
  selectedAttributes: CategoryAttributeSaveRow[] | null | undefined
}): CategoryAttributeSaveValidationResult => {
  const catalog = resolveCategoryAttributeCatalog(
    params.categoryAttributes,
    params.categoryAttributeCatalogSnapshot
  )
  const selectedAttributes = Array.isArray(params.selectedAttributes)
    ? params.selectedAttributes
    : []

  if (
    shouldBlockSaveForUnavailableCategoryAttributeCatalog({
      categoryId: params.categoryId,
      isAttributeLoading: params.isAttributeLoading,
      categoryAttributes: catalog,
      selectedAttributes,
    })
  ) {
    return { ok: false, reason: 'catalog_unavailable' }
  }

  const missing = getMissingRequiredCategoryAttribute({
    categoryAttributes: catalog,
    selectedAttributes,
  })
  if (missing) {
    return { ok: false, reason: 'missing_required', attribute: missing }
  }

  return { ok: true }
}
